import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import * as chatApi from './chatApi'
import {
	finishMuteChange,
	finishMuteLoad,
	initialMuteSetting,
	isMuteEnabled,
	startMuteChange,
	startMuteLoad,
	type ChatMuteSetting,
} from './core'

/**
 * Drives the Mute Chat notifications switch: reads my mute from the server and sets it,
 * keeping the switch on `ChatMuteSetting`. Loads when `tripId` is given. Ported from iOS
 * `ChatMuteViewModel`.
 */
export const useChatMute = (tripId: string | undefined) => {
	const [setting, setSetting] = useState<ChatMuteSetting>(initialMuteSetting)
	const settingRef = useRef(setting)

	const apply = useCallback((next: ChatMuteSetting) => {
		settingRef.current = next
		setSetting(next)
	}, [])

	useEffect(() => {
		if (!tripId) return
		apply(initialMuteSetting)
		const load = startMuteLoad(settingRef.current)
		chatApi
			.fetchMute(tripId)
			.then(
				({ isMuted }) => isMuted,
				() => null,
			)
			.then(isMuted => apply(finishMuteLoad(settingRef.current, load, isMuted)))
	}, [apply, tripId])

	const setMuted = useCallback(
		(isMuted: boolean) => {
			if (!tripId) return
			const started = startMuteChange(settingRef.current, isMuted)
			apply(started.setting)
			chatApi
				.setMute(tripId, isMuted)
				.then(
					answer => answer.isMuted,
					() => null,
				)
				.then(answer => {
					const finished = finishMuteChange(settingRef.current, started.change, answer)
					apply(finished.setting)
					if (finished.flippedBack) toast.error('Failed to update Chat notifications. Please try again.')
				})
		},
		[apply, tripId],
	)

	return { isMuted: setting.isMuted, isEnabled: isMuteEnabled(setting), setMuted }
}
